import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  assignFilterGroups,
  filterGroupsShellQuery,
  removeFilterGroup,
  type FilterGroupsRow,
} from '../../api/settingsEaseedeskQuickFilters'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Assign filters to groups.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * quick_filters_groups/index.php` + `list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/tickets/quick_filters_groups/index-1.js`.
 *
 * Ecranul vechi cerea grupurile FIECARUI filtru intr-o interogare separata;
 * aici vin toate cu lista, dintr-una singura.
 */
export function QuickFiltersGroupsPage() {
  const queryClient = useQueryClient()
  const page = useQuery(filterGroupsShellQuery())
  const [assigning, setAssigning] = useState<FilterGroupsRow | null>(null)
  const [chosen, setChosen] = useState<number[]>([])

  useEffect(() => {
    if (assigning === null) setChosen([])
  }, [assigning])

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'quick_filters_groups'] })
  }

  const assign = useMutation({
    mutationFn: () => assignFilterGroups(assigning?.ID ?? 0, chosen),
    onSuccess: () => {
      toast('Groups assigned.', 'Quick filters')
      setAssigning(null)
      reload()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const drop = useMutation({
    mutationFn: (v: { filter: number; group: number }) => removeFilterGroup(v.filter, v.group),
    onSuccess: reload,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="This screen" />
  }
  if (!page.data) return <Spinner />

  return (
    <div>
      <h4>
        Assign <strong>filters</strong> to groups
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Create or edit default <strong>quick filters</strong>
      </label>
      <hr />

      <div className="list-group">
        <table className="table table-hover settings-table">
          <thead>
            <tr>
              <th className="align-left">Filter name</th>
              <th className="align-left">Groups</th>
              <th width={100}>Action</th>
            </tr>
          </thead>
          <tbody id="assign-filter-groups-list">
            {page.data.filters.map((filter) => (
              <tr key={filter.ID}>
                <td>
                  <strong>
                    <i className="fas fa-filter" aria-hidden="true"></i> {filter.filter_name}
                  </strong>
                </td>
                <td>
                  {filter.groups.map((group) => (
                    <label className="label bg-inverse" key={group.ID}>
                      {group.group_name}{' '}
                      <a
                        href="#"
                        className="color-white remove-filter-group"
                        data-filter={filter.ID}
                        data-group={group.ID}
                        onClick={(e) => {
                          e.preventDefault()
                          drop.mutate({ filter: filter.ID, group: group.ID })
                        }}
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </a>
                    </label>
                  ))}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-xs btn-default"
                    data-filter={filter.ID}
                    onClick={() => setAssigning(filter)}
                  >
                    <i className="fas fa-plus" aria-hidden="true"></i> Add group
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        id="assignGroupModal"
        labelledBy="assignGroupModalLabel"
        open={assigning !== null}
        onClose={() => setAssigning(null)}
        title="Add group"
      >
        <form
          id="assignFilterGroupForm"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (chosen.length === 0) return
            assign.mutate()
          }}
        >
          <div className="form-group">
            <label>Filter name</label>
            <p className="form-control-static" id="filter-name">
              {assigning?.filter_name ?? ''}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="filter-groups">Group</label>
            <select
              multiple
              className="form-control"
              id="filter-groups"
              name="filter-groups[]"
              value={chosen.map(String)}
              onChange={(e) => setChosen(Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}
            >
              {page.data.groups.map((group) => (
                <option key={group.ID} value={String(group.ID)}>
                  {group.department_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={() => setAssigning(null)}>
              Close
            </button>{' '}
            <button
              type="submit"
              className="btn btn-primary submit-filter-group"
              disabled={chosen.length === 0 || assign.isPending}
            >
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
